import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { exportService } from '../services/exportService';
import type { Student } from '../types/student';
import { Download, Table } from 'lucide-react';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  students: Student[];
  currentFilters: any;
  searchTerm: string;
}

type ExportFormat = 'sheets' | 'csv' | 'excel';

export function ExportDialog({
  open,
  onOpenChange,
  students,
  currentFilters,
  searchTerm
}: ExportDialogProps) {
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('sheets');
  const [exportConfig, setExportConfig] = useState({
    // Filtros de dados
    accessFilter: 'all', // Novo campo para filtro de acesso
    courseStatus: 'all',
    interactionLevel: 'all',
    
    // Colunas para exportar
    columns: {
      personalInfo: true,
      courses: true,
      interactions: true,
      metrics: true,
      dates: true
    }
  });

  const handleExport = async () => {
    try {
      setLoading(true);

      // Prepara os filtros baseados na configuração
      const exportFilters = {
        ...currentFilters,
        accessFilter: exportConfig.accessFilter,
        courseStatus: exportConfig.courseStatus,
        interactionLevel: exportConfig.interactionLevel
      };

      // Busca dados filtrados
      const filteredData = await exportService.getAllFilteredData(searchTerm, exportFilters);

      // Configura as colunas para exportação
      const exportOptions = {
        columns: {
          ...exportConfig.columns
        }
      };

      // Exporta no formato selecionado
      switch (exportFormat) {
        case 'sheets':
          await exportService.exportToGoogleSheets(filteredData, exportOptions);
          break;
        case 'csv':
          await exportService.exportToCSV(filteredData, exportOptions);
          break;
        case 'excel':
          await exportService.exportToExcel(filteredData, exportOptions);
          break;
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      // TODO: Mostrar toast de erro
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Exportar Dados</DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Formato de Exportação */}
          <div className="space-y-4">
            <h3 className="font-medium">Formato de Exportação</h3>
            <Tabs
              value={exportFormat}
              onValueChange={(value) => setExportFormat(value as ExportFormat)}
              className="w-full"
            >
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="sheets" className="flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  Google Sheets
                </TabsTrigger>
                <TabsTrigger value="csv" className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  CSV
                </TabsTrigger>
                <TabsTrigger value="excel" className="flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  Excel
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Filtros de Dados */}
          <div className="space-y-4">
            <h3 className="font-medium">Filtros de Dados</h3>

            <div className="space-y-2">
              <Label>Período de Acesso</Label>
              <Select
                value={exportConfig.accessFilter}
                onValueChange={(value) =>
                  setExportConfig(prev => ({ ...prev, accessFilter: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os alunos</SelectItem>
                  <SelectItem value="nao15dias">Não acessaram últimos 15 dias</SelectItem>
                  <SelectItem value="nao30dias">Não acessaram últimos 30 dias</SelectItem>
                  <SelectItem value="nao90dias">Não acessaram últimos 90 dias</SelectItem>
                  <SelectItem value="nunca">Nunca acessaram</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status do Curso</Label>
              <Select
                value={exportConfig.courseStatus}
                onValueChange={(value) =>
                  setExportConfig(prev => ({ ...prev, courseStatus: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">Em andamento</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="pending">Não iniciado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Nível de Interação</Label>
              <Select
                value={exportConfig.interactionLevel}
                onValueChange={(value) =>
                  setExportConfig(prev => ({ ...prev, interactionLevel: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o nível" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="high">Alto engajamento</SelectItem>
                  <SelectItem value="medium">Médio engajamento</SelectItem>
                  <SelectItem value="low">Baixo engajamento</SelectItem>
                  <SelectItem value="none">Sem engajamento</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Seleção de Colunas */}
          <div className="space-y-4">
            <h3 className="font-medium">Colunas para Exportar</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="personalInfo">Informações Pessoais</Label>
                <Switch
                  id="personalInfo"
                  checked={exportConfig.columns.personalInfo}
                  onCheckedChange={(checked) =>
                    setExportConfig(prev => ({
                      ...prev,
                      columns: { ...prev.columns, personalInfo: checked }
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="courses">Dados dos Cursos</Label>
                <Switch
                  id="courses"
                  checked={exportConfig.columns.courses}
                  onCheckedChange={(checked) =>
                    setExportConfig(prev => ({
                      ...prev,
                      columns: { ...prev.columns, courses: checked }
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="interactions">Interações</Label>
                <Switch
                  id="interactions"
                  checked={exportConfig.columns.interactions}
                  onCheckedChange={(checked) =>
                    setExportConfig(prev => ({
                      ...prev,
                      columns: { ...prev.columns, interactions: checked }
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="metrics">Métricas</Label>
                <Switch
                  id="metrics"
                  checked={exportConfig.columns.metrics}
                  onCheckedChange={(checked) =>
                    setExportConfig(prev => ({
                      ...prev,
                      columns: { ...prev.columns, metrics: checked }
                    }))
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="dates">Datas</Label>
                <Switch
                  id="dates"
                  checked={exportConfig.columns.dates}
                  onCheckedChange={(checked) =>
                    setExportConfig(prev => ({
                      ...prev,
                      columns: { ...prev.columns, dates: checked }
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleExport} disabled={loading}>
            {loading ? 'Exportando...' : `Exportar para ${
              exportFormat === 'sheets' ? 'Google Sheets' :
              exportFormat === 'csv' ? 'CSV' : 'Excel'
            }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}